<template>
  <div class="time-range-filter flex flex-wrap items-center gap-2">
    <div class="flex flex-wrap items-center gap-1 border border-gray-200 bg-gray-50/80 p-1">
      <button
        v-for="preset in presets"
        :key="preset.value"
        type="button"
        class="px-3 py-1.5 text-sm font-medium transition-colors"
        :class="current?.preset === preset.value
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-gray-600 hover:bg-white/60 hover:text-gray-800'"
        @click="applyPreset(preset)">
        {{ preset.label }}
      </button>
      <button
        type="button"
        class="px-3 py-1.5 text-sm font-medium transition-colors"
        :class="current?.preset === 'custom'
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-gray-600 hover:bg-white/60 hover:text-gray-800'"
        @click="openCustomModal">
        {{ t('time_range_filter.custom') }}
      </button>
    </div>

    <span class="text-sm text-gray-500">
      {{ rangeSummary }}
    </span>

    <TimeRangeCustomModal
      :is-open="showCustom"
      :initial-start="customModalInitialStart"
      :initial-end="customModalInitialEnd"
      @close="showCustom = false"
      @apply="applyCustom"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from '@/composable/useI18n'
import TimeRangeCustomModal from './TimeRangeCustomModal.vue'
import type { TimeRangeValue } from './types'
import {
  buildHoursRange,
  formatRangeSummary,
  nowRounded,
} from '@/utils/date/date-time'

const { t } = useI18n()

const presets = computed(() => [
  { value: '24h' as const, label: t('time_range_filter.presets.24h'), hours: 24 },
  { value: '7d' as const, label: t('time_range_filter.presets.7d'), hours: 24 * 7 },
])

const props = withDefaults(
  defineProps<{
    defaultPreset?: '24h' | '7d'
    initialRange?: TimeRangeValue | null
  }>(),
  {
    defaultPreset: '24h',
    initialRange: null,
  },
)

const emit = defineEmits<{
  (e: 'change', value: TimeRangeValue): void
}>()

const showCustom = ref(false)
const current = ref<TimeRangeValue | null>(null)

function buildRange(preset: '24h' | '7d' | 'custom', start: Date, end: Date): TimeRangeValue {
  return { start, end, preset }
}

function applyPreset(preset: (typeof presets.value)[number]) {
  const { start, end } = buildHoursRange(preset.hours)
  const value = buildRange(preset.value, start, end)
  current.value = value
  emit('change', value)
}

function openCustomModal() {
  showCustom.value = true
}

function applyCustom(start: Date, end: Date) {
  const value = buildRange('custom', start, end)
  current.value = value
  emit('change', value)
  showCustom.value = false
}

const customModalInitialStart = computed(() => {
  if (current.value?.preset === 'custom') return current.value.start
  const end = nowRounded()
  return new Date(end.getTime() - 24 * 60 * 60 * 1000)
})

const customModalInitialEnd = computed(() => {
  if (current.value?.preset === 'custom') return current.value.end
  return nowRounded()
})

const rangeSummary = computed(() => {
  const value = current.value
  if (!value) return ''
  return formatRangeSummary(value.start, value.end)
})

onMounted(() => {
  const initial = props.initialRange
  if (initial != null && initial.start != null && initial.end != null) {
    current.value = {
      start: new Date(initial.start),
      end: new Date(initial.end),
      preset: initial.preset ?? '24h',
    }
    return
  }
  const preset = presets.value.find((item) => item.value === props.defaultPreset) ?? presets.value[0]
  applyPreset(preset)
})
</script>
